import { handleResponse } from "./service-helpers";

const NBH_MEMBERS_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/members`;

export type PointsTransactionSourceType = "MANUAL" | "AUTOMATIC" | "IMPORT" | "SPECIAL_ASSIGNMENT";

export type PointsTransactionDirection = "CREDIT" | "DEBIT";

export interface MemberPointsStatementMember {
    id: string;
    memberNo: number;
    firstName: string;
    lastName: string;
    membershipStart: string;
    membershipEnd: string | null;
    resigned: boolean;
}

export interface MemberPointsStatementEntry {
    id: string;
    sourceType: PointsTransactionSourceType;
    direction: PointsTransactionDirection;
    amount: number;
    description: string;
    transactionTimestamp: string;
    bookedAt: string;
    bookedByName: string | null;
    orderId: string | null;
    orderNumber: string | null;
    specialAssignmentBookingId: string | null;
}

export interface MemberPointsStatement {
    member: MemberPointsStatementMember;
    openingBalance: number;
    closingBalance: number;
    from: string | null;
    to: string | null;
    search: string | null;
    entries: MemberPointsStatementEntry[];
}

export interface StatementDateRange {
    from: string;
    to: string;
}

function statementQuery(range?: StatementDateRange | null, search?: string | null): string {
    const params = new URLSearchParams();
    if (range) {
        params.set('from', range.from);
        params.set('to', range.to);
    }
    if (search && search.trim()) {
        params.set('search', search.trim());
    }
    const query = params.toString();
    return query ? `?${query}` : '';
}

export async function getMemberPointsStatement(
    token: string,
    memberId: string,
    range?: StatementDateRange | null,
    search?: string | null,
): Promise<MemberPointsStatement> {
    const url = `${NBH_MEMBERS_BASE_URL}/${memberId}/points-statement${statementQuery(range, search)}`;
    const response = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    return await handleResponse<MemberPointsStatement>(response) as MemberPointsStatement;
}

async function downloadStatementFile(
    token: string,
    memberId: string,
    format: 'excel' | 'pdf',
    fallbackFilename: string,
    range?: StatementDateRange | null,
    search?: string | null,
): Promise<{ blob: Blob; filename: string }> {
    const url = `${NBH_MEMBERS_BASE_URL}/${memberId}/points-statement/${format}${statementQuery(range, search)}`;
    const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
        throw new Error(`Member points statement ${format} export failed`);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
    return { blob, filename: filenameMatch?.[1] ?? fallbackFilename };
}

export function downloadMemberPointsStatementExcel(
    token: string,
    memberId: string,
    range?: StatementDateRange | null,
    search?: string | null,
): Promise<{ blob: Blob; filename: string }> {
    return downloadStatementFile(token, memberId, 'excel', 'punktekonto.xlsx', range, search);
}

export function downloadMemberPointsStatementPdf(
    token: string,
    memberId: string,
    range?: StatementDateRange | null,
    search?: string | null,
): Promise<{ blob: Blob; filename: string }> {
    return downloadStatementFile(token, memberId, 'pdf', 'punktekonto.pdf', range, search);
}
